// Typed client for the admin project-ingest pipeline (epic PROP-42).
// Backs the Add-Project wizard (PROP-67). Every endpoint here is
// already shipped server-side (dispatch PROP-58, rewrite PROP-60,
// leakscan PROP-62, commit PROP-64, advance PROP-67); this module is
// the one place the wizard talks to them.

import { api } from "./api";

// ─── session ────────────────────────────────────────────────────────

export type StagingSource = "cos" | "stacked" | "manual";

// Forward step sequence — mirrors store.stagingStateOrder (the server
// enforces it; this is for display + resume). committed / abandoned are
// terminal and not part of the wizard's forward path.
export type StagingState =
  | "crawl"
  | "extract"
  | "rewrite"
  | "enrich"
  | "review"
  | "leak_check"
  | "committed"
  | "abandoned";

export interface StagingSession {
  id: string;
  slug: string;
  source: StagingSource;
  source_url?: string;
  state: StagingState;
  created_by_admin_id: string;
  created_at: string;
  updated_at: string;
  last_step_payload: unknown;
  rewrite_payload?: unknown;
}

export interface DispatchResult {
  session_id: string;
  slug: string;
  state: StagingState;
  created: boolean;
}

export interface DispatchInput {
  source: StagingSource;
  url?: string;
  manual?: { slug?: string; name?: string };
}

export const dispatchProject = (input: DispatchInput) =>
  api.post<DispatchResult>("/admin/dataengine/project/dispatch", input);

export const getSession = (id: string) =>
  api.get<StagingSession>(`/admin/dataengine/project/session/${encodeURIComponent(id)}`);

export const listSessions = (state?: StagingState) =>
  api.get<{ sessions: StagingSession[] }>(
    `/admin/dataengine/project/sessions${state ? `?state=${encodeURIComponent(state)}` : ""}`,
  ).then((r) => r.sessions ?? []);

export const advanceSession = (id: string, state: StagingState) =>
  api.post<StagingSession>(`/admin/dataengine/project/session/${encodeURIComponent(id)}/advance`, { state });

export const abandonSession = (id: string) =>
  api.post<StagingSession>(`/admin/dataengine/project/session/${encodeURIComponent(id)}/abandon`, {});

// ─── rewrite (PROP-60) ──────────────────────────────────────────────

export interface RewriteFields {
  description: string;
  tagline: string;
  key_features: string;
}

export interface RewritePayload {
  raw: RewriteFields;
  rewritten: RewriteFields;
  diff_html: { description: string; tagline: string; key_features: string };
}

export const runRewrite = (id: string) =>
  api.post<RewritePayload>(`/admin/dataengine/project/session/${encodeURIComponent(id)}/rewrite`, {});

export const getRewritePreview = (id: string) =>
  api.get<RewritePayload>(`/admin/dataengine/project/session/${encodeURIComponent(id)}/rewrite_preview`);

export const applyRewrite = (id: string) =>
  api.post<{ ok: boolean }>(`/admin/dataengine/project/session/${encodeURIComponent(id)}/rewrite_apply`, {});

// ─── leak-scan (PROP-62) ────────────────────────────────────────────

export interface LeakHit {
  id: string;
  field: string;
  match: string;
  snippet: string;
  accepted: boolean;
}

export interface LeakScanResult {
  slug: string;
  hits: LeakHit[];
  unresolved: number;
}

export const runLeakScan = (id: string) =>
  api.get<LeakScanResult>(`/admin/dataengine/project/session/${encodeURIComponent(id)}/leakscan`)
    .then((r) => ({ ...r, hits: r.hits ?? [] }));

export const acceptLeakHit = (id: string, hitId: string, reason: string) =>
  api.post<{ ok: boolean; hit_id: string }>(
    `/admin/dataengine/project/session/${encodeURIComponent(id)}/leakscan/accept`,
    { hit_id: hitId, reason },
  );

// ─── commit (PROP-64) ───────────────────────────────────────────────

export interface CommitResult {
  slug: string;
  state: StagingState;
  audit_id: number;
  committed: boolean;
}

export const commitSession = (id: string) =>
  api.post<CommitResult>(`/admin/dataengine/project/session/${encodeURIComponent(id)}/commit`, {});

// ─── wizard step mapping (pure) ─────────────────────────────────────

// The seven wizard steps, in order. "source" is the pre-dispatch form;
// once a session exists it maps to one of extract → … → commit.
export const WIZARD_STEPS = [
  "source",
  "extract",
  "rewrite",
  "enrich",
  "review",
  "leak",
  "commit",
] as const;
export type WizardStep = (typeof WIZARD_STEPS)[number];

// stepFromState maps a persisted session state to the wizard step the
// operator should resume on. The backend only persists the coarse
// pipeline state (crawl/extract/rewrite/enrich/review + terminal); the
// wizard's finer "leak" / "commit" steps both sit on top of 'review',
// so a review-state session resumes at the Leak-scan gate (the step
// before commit). A committed session resumes at the done/commit step.
export function stepFromState(state: StagingState): WizardStep {
  switch (state) {
    case "crawl":
    case "extract":
      return "extract";
    case "rewrite":
      return "rewrite";
    case "enrich":
      return "enrich";
    case "review":
    case "leak_check":
      return "leak";
    case "committed":
      return "commit";
    case "abandoned":
      // An abandoned session has nothing to resume; the wizard treats
      // it as "start over" by returning to source.
      return "source";
    default:
      return "extract";
  }
}

// The persisted state a given wizard step should advance the session to
// before its work begins. null = no advance needed (source has no
// session yet; leak/commit ride on 'review'). Used by the wizard to
// keep server state in step with the operator's progress for resume.
export function stateForStep(step: WizardStep): StagingState | null {
  switch (step) {
    case "rewrite":
      return "rewrite";
    case "enrich":
      return "enrich";
    case "leak":
      return "review";
    default:
      return null;
  }
}

// stepIndex / labels for the progress rail.
export function stepIndex(step: WizardStep): number {
  return WIZARD_STEPS.indexOf(step);
}

export const STEP_LABELS: Record<WizardStep, string> = {
  source: "Source",
  extract: "Extract",
  rewrite: "Rewrite",
  enrich: "Enrich",
  review: "Review",
  leak: "Leak scan",
  commit: "Commit",
};
